import { useEffect } from "react";

import { UseQueryOptions, UseQueryResult, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useAlert } from "capitalrx-components-2.0/lib/general";
import { useAuthToken } from "capitalrx-shell";

import { getCoverageTypeAPI, getPharmacyAPI, listPharmacyAPI, getWhitelabelAPI } from "@/api/axios";

interface ErrorResponse {
  message?: string;
}

type Options = Omit<UseQueryOptions, "queryKey">;
export type AxiosErrorType = AxiosError<ErrorResponse, unknown>;

type UseGetHook<Request, Response> = (request?: Request, options?: Options) => UseQueryResult<Response, AxiosError>;

const createGetHook = <Request, Response>(
  url: string,
  apiCall: (request: Request, authToken: string) => Promise<Response>,
  defaultOptions?: Options,
): UseGetHook<Request, Response> => {
  const useGetQuery = (request, options) => {
    const [authToken] = useAuthToken();
    const appliedOptions = defaultOptions ? defaultOptions : {};
    const showAlert = useAlert();

    const call: UseQueryResult<Response, AxiosErrorType> = useQuery<Response, AxiosErrorType>({
      queryKey: [url, request],
      queryFn: async () => apiCall(request, authToken),
      ...appliedOptions,
      ...options,
    });

    const error = call.error as AxiosErrorType;

    useEffect(() => {
      if (call.isError) {
        showAlert({
          message: error.response?.data?.message || error.message,
          type: "error",
          style: "toast",
        });
      }
    }, [call.isError, call.error, showAlert]);

    return call;
  };

  return useGetQuery;
};


// GET
export const useGetCoverageType = createGetHook("/coverage_type", getCoverageTypeAPI);
export const useGetPharmacy = createGetHook("/pharmacy/get", getPharmacyAPI);
export const useGetWhitelabel = createGetHook("/whitelabel/get", getWhitelabelAPI);

// LIST
export const useListPharmacy = createGetHook("/pharmacy/list", listPharmacyAPI);
